(function () {
  'use strict';

  // Live preview of the round screen emblem. Drawn on a canvas; nothing leaves the visitor's device.
  var MODES = ['text', 'rings', 'sweep', 'bars'];
  var MODE_NAMES = { text: 'your text', rings: 'pulsing rings', sweep: 'a sweeping beam', bars: 'equalizer bars', image: 'your image' };
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FONT = '"Barlow Condensed", "Arial Narrow", sans-serif';
  var CYCLE_TEXT = 2.5;
  var CYCLE_RINGS = 5;
  var CYCLE_STOP = 60;
  var FILE_SIZE = 480;
  var FILE_NAME = 'minkmods-design.jpg';

  function track(name, data) {
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
        var payload = { page: location.pathname };
        Object.keys(data || {}).forEach(function (k) { payload[k] = data[k]; });
        Shopify.analytics.publish('minkmods:' + name, payload);
      }
    } catch (e) {
      /* tracking never blocks the page */
    }
  }

  function announce(message) {
    var live = document.querySelector('[data-cart-live]');
    if (live) live.textContent = message || '';
  }

  // Each block runs on its own: one that throws is logged, and the rest of the page still gets its guards.
  function guard(fn) {
    try {
      return fn();
    } catch (e) {
      if (window.console && console.error) console.error('minkmods.js', e);
    }
  }

  function each(selector, fn) {
    Array.prototype.forEach.call(document.querySelectorAll(selector), function (el) {
      guard(function () { fn(el); });
    });
  }

  window.MinkMods = window.MinkMods || {};
  var inApp = /BytedanceWebview|musical_ly|TikTok|Instagram|FBAN|FBAV/i.test(navigator.userAgent || '');

  /* ---------- Required fields and tick boxes: nothing goes in the cart until each one is filled ---------- */

  function fieldMissing(field) {
    if (field.disabled) return false;
    if (field.type === 'checkbox') return !field.checked;
    return !String(field.value || '').trim();
  }

  function missingFields(form) {
    return Array.prototype.filter.call(form.querySelectorAll('[data-required-property]'), fieldMissing);
  }

  function firstMissing(form) {
    return missingFields(form)[0] || null;
  }

  function vehicleProblem(field) {
    if (!field || field.disabled || !field.hasAttribute('data-vehicle-details') || !String(field.value || '').trim()) return '';
    if (field.getAttribute('data-fit-blocked')) return field.getAttribute('data-fit-blocked');
    var text = String(field.value).trim();
    if (!/\b(?:19|20)\d{2}\b/.test(text)) return 'Add the four-digit model year, for example 2021 BMW 330i.';
    var car = text.replace(/\b(?:19|20)\d{2}\b/g, '').trim();
    if (!/[a-z]{2}/i.test(car) || car.split(/\s+/).filter(Boolean).length < 2) return 'Add both the make and model with the year, for example 2021 BMW 330i.';
    return '';
  }

  // Custom validation keeps vehicle details and gift-recipient fields usable without browser pop-ups.
  function firstInvalid(form) {
    return invalidFields(form)[0] || null;
  }

  function invalidFields(form) {
    return Array.prototype.filter.call(form.querySelectorAll('[data-vehicle-details], [data-gift-card-fields] input, [data-qty-input]'), function (field) {
      return !field.disabled && !!fieldProblem(field);
    });
  }

  function finderBlocked(form) {
    var finder = form.querySelector('[data-fit-finder-select]');
    return finder && finder.value === 'none' ? finder : null;
  }

  // The words of an error sit right under the field they are about, so they are on screen with it.
  var errorSeq = 0;
  function showFieldError(field, message) {
    field.setAttribute('aria-invalid', 'true');
    // The size finder has its own note right under it, which already says why.
    if (field.hasAttribute('data-fit-finder-select')) return;
    var out = field._mmError;
    if (!out) {
      out = document.createElement('p');
      out.className = 'cart-item__error pdp__field-error';
      out.id = 'FieldError' + (++errorSeq);
      var holder = field.closest('.pdp__check, .pdp__qty');
      (holder || field).insertAdjacentElement('afterend', out);
      field._mmError = out;
      var described = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      described.push(out.id);
      field.setAttribute('aria-describedby', described.join(' '));
    }
    out.textContent = message;
    out.hidden = false;
    if (field.hasAttribute('data-fit-hints') && field.getAttribute('data-fit-blocked')) {
      var hint = field.closest('.pdp__field').querySelector('[data-fit-hint]');
      if (hint) hint.textContent = '';
    }
  }

  function clearFieldError(field) {
    if (field._mmError) field._mmError.hidden = true;
    field.removeAttribute('aria-invalid');
  }

  function fieldHasError(field) {
    if (field.hasAttribute('data-fit-finder-select')) return field.getAttribute('aria-invalid') === 'true';
    return !!field._mmError && !field._mmError.hidden;
  }

  function fieldProblem(field) {
    if (field.disabled) return '';
    if (field.hasAttribute('data-fit-finder-select')) {
      return field.value === 'none' ? field.getAttribute('data-nofit') || 'This one won\'t fit that car.' : '';
    }
    if (field.hasAttribute('data-required-property') && fieldMissing(field)) {
      return field.getAttribute('data-required-message') || 'Fill this in first';
    }
    if (vehicleProblem(field)) return vehicleProblem(field);
    if (field.hasAttribute('data-qty-input')) {
      var quantity = Number(field.value);
      var minimum = Math.max(1, Number(field.getAttribute('min')) || 1);
      var maximum = field.getAttribute('max') ? Number(field.getAttribute('max')) : Infinity;
      if (!String(field.value).trim() || !isFinite(quantity) || Math.floor(quantity) !== quantity || quantity < minimum || quantity > maximum) {
        return 'Enter a whole-number quantity of at least ' + minimum + (isFinite(maximum) ? ' and no more than ' + maximum : '') + '.';
      }
    }
    if (field.closest('[data-gift-card-fields]') && !field.disabled && field.value && field.validity && !field.validity.valid) {
      return field.type === 'date'
        ? 'Pick a send date from today up to 90 days ahead.'
        : 'That email address doesn\'t look right. Check it and try again.';
    }
    return '';
  }

  // One line by the button: how many things are left, and which.
  function leftSummary(fields) {
    var names = [];
    var ticks = 0;
    fields.forEach(function (field) {
      if (field.type === 'checkbox') ticks += 1;
      else names.push(field.getAttribute('data-required-name') || (field.hasAttribute('data-fit-finder-select') ? 'a car that fits' : 'a required field'));
    });
    if (ticks) names.push(ticks === 1 ? 'the confirmation box' : ticks + ' confirmation boxes');
    return fields.length + (fields.length === 1 ? ' thing left: ' : ' things left: ') + names.join(', ') + '.';
  }

  function showLeft(form, fields) {
    var errorEl = form.querySelector('[data-cart-error]');
    if (!errorEl) return;
    if (!fields.length) {
      errorEl.hidden = true;
      errorEl.removeAttribute('data-left');
      return;
    }
    var text = leftSummary(fields);
    errorEl.textContent = text;
    errorEl.setAttribute('data-left', text);
    errorEl.hidden = false;
  }

  /* Nothing picked yet: take the buyer to the options and say what to pick. The sticky bar uses it too. */
  function pointAtOption(fieldset) {
    var values = fieldset.getAttribute('data-option-values');
    var message = values
      ? 'Pick ' + values + ' to continue.'
      : 'Pick your ' + (fieldset.getAttribute('data-option-name') || 'option') + ' to continue.';
    var out = fieldset._mmError;
    if (!out) {
      out = document.createElement('p');
      out.className = 'cart-item__error pdp__field-error';
      out.id = 'OptionError' + (++errorSeq);
      fieldset.appendChild(out);
      fieldset._mmError = out;
    }
    out.textContent = message;
    out.hidden = false;
    fieldset.classList.add('is-missing');
    fieldset.querySelectorAll('input[type="radio"]').forEach(function (radio) {
      radio.setAttribute('aria-invalid', 'true');
      var described = (radio.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (described.indexOf(out.id) === -1) described.push(out.id);
      radio.setAttribute('aria-describedby', described.join(' '));
    });
    announce(message);
    fieldset.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    var first = fieldset.querySelector('input[type="radio"]:not(:disabled)');
    if (first) first.focus({ preventScroll: true });
  }
  window.MinkMods.pointAtOption = pointAtOption;

  function openOption(zone) {
    return Array.prototype.find.call(zone.querySelectorAll('[data-option-fieldset]'), function (fs) {
      return !fs.querySelector('input[type="radio"]:checked');
    }) || null;
  }

  function purchaseProblems(form) {
    var problems = [];
    var blocked = finderBlocked(form);
    if (blocked) problems.push(blocked);
    missingFields(form).forEach(function (field) { problems.push(field); });
    invalidFields(form).forEach(function (field) { if (problems.indexOf(field) === -1) problems.push(field); });
    return problems.sort(function (a, b) {
      return a.compareDocumentPosition(b) & 4 ? -1 : 1;
    });
  }

  // The main button and sticky button use the same required-field checks.
  function validateProductPurchase(form) {
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return true;
    var zone = form.closest('[data-product-zone]');
    if (!zone) return true;
    var idInput = form.querySelector('[data-variant-id]');
    if (idInput && !idInput.value) {
      var open = openOption(zone);
      if (open) {
        pointAtOption(open);
        return false;
      }
    }
    var section = zone.closest('section');
    var json = section && section.querySelector('[data-variant-json]');
    var variants;
    try { variants = json ? JSON.parse(json.textContent) : null; } catch (error) { variants = null; }
    var variant = variants && idInput && variants.find(function (entry) { return String(entry.id) === String(idInput.value); });
    if (!idInput || !idInput.value || (variants && (!variant || !variant.available))) {
      var status = form.querySelector('[data-variant-status]');
      var message = variant && !variant.available ? 'This option is sold out. Choose an available option before adding to cart.' : 'This option combination is unavailable. Choose different options before adding to cart.';
      if (status) { status.textContent = message; status.hidden = false; }
      announce(message);
      var option = zone.querySelector('[data-option-fieldset]');
      var target = option && option.querySelector('input[type="radio"]:not(:disabled)');
      if (target) target.focus({ preventScroll: true });
      (option || form).scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      return false;
    }
    // Every problem is marked in one pass, each with its own message, so one tap shows all that is left.
    var problems = purchaseProblems(form);
    if (!problems.length) return true;
    problems.forEach(function (field) { showFieldError(field, fieldProblem(field)); });
    showLeft(form, problems);
    var first = problems[0];
    announce(problems.length === 1 ? fieldProblem(first) : leftSummary(problems));
    first.focus({ preventScroll: true });
    (first.closest('.pdp__check, .pdp__field') || first).scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    return false;
  }
  window.MinkMods.validateProductPurchase = validateProductPurchase;

  window.MinkMods.purchaseState = function (form) {
    var zone = form && form.closest('[data-product-zone]');
    if (!zone) return null;
    var atc = zone.querySelector('[data-atc]');
    var blocked = finderBlocked(form);
    if (blocked) return { label: 'Check fit', disabled: false };
    if (atc && atc.disabled) return { label: atc.textContent.trim(), disabled: true };
    var idInput = form.querySelector('[data-variant-id]');
    if (idInput && !idInput.value) return { label: openOption(zone) ? 'Choose options' : 'Choose another option', disabled: false };
    var field = purchaseProblems(form)[0];
    if (field) {
      return {
        label: field.hasAttribute('data-vehicle-details') ? 'Complete car details'
          : field.type === 'checkbox' ? 'Confirm details'
          : field.getAttribute('data-required-name') === 'your text' ? 'Add your text'
          : field.tagName === 'SELECT' ? 'Choose options' : 'Complete details',
        disabled: false
      };
    }
    return { label: 'Add to cart', disabled: false };
  };

  // Runs before cart.js (window comes first in the capture phase), so an unticked box can never slip through.
  window.addEventListener('submit', function (event) {
    if (validateProductPurchase(event.target)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  // The form keeps the browser's own required checks until this point, so a page whose scripts never load
  // still refuses an empty field or an unticked box. From here the checks above take over.
  each('[data-product-zone] form[action*="/cart/add"]', function (form) { form.noValidate = true; });

  // A fixed field loses its message at once, and the line by the button counts down with it.
  function onEdit(event) {
    var field = event.target;
    if (!field || !field.closest) return;
    var set = field.closest('[data-option-fieldset]');
    if (set && set._mmError && set.querySelector('input[type="radio"]:checked')) {
      set._mmError.hidden = true;
      set.classList.remove('is-missing');
      set.querySelectorAll('input[type="radio"]').forEach(function (radio) { radio.removeAttribute('aria-invalid'); });
    }
    var form = field.closest('form');
    if (!form || !form.closest('[data-product-zone]')) return;
    Array.prototype.forEach.call(form.querySelectorAll('input, select, textarea'), function (input) {
      if (!fieldHasError(input)) return;
      var problem = fieldProblem(input);
      if (!problem) clearFieldError(input);
      else if (input._mmError) input._mmError.textContent = problem;
    });
    var errorEl = form.querySelector('[data-cart-error]');
    if (!errorEl || errorEl.hidden || errorEl.getAttribute('data-left') !== errorEl.textContent) return;
    showLeft(form, Array.prototype.filter.call(form.querySelectorAll('input, select, textarea'), fieldHasError));
  }
  document.addEventListener('input', onEdit);
  document.addEventListener('change', onEdit);

  /* ---------- Shop Pay / Buy it now skips the cart: it is locked in the markup and only ever unlocked here ---------- */

  each('[data-product-zone]', function (zone) {
    var express = zone.querySelector('[data-express-checkout]');
    var hint = zone.querySelector('[data-express-hint]');
    var agree = zone.querySelector('[data-express-agree]');
    if (!express) return;
    var form = express.closest('form');
    var idInput = form && form.querySelector('[data-variant-id]');
    if (!form || !idInput) return;

    function sync() {
      var unpicked = !idInput.value;
      var atc = form.querySelector('[data-atc]');
      var unavailable = !!atc && atc.disabled;
      var unfilled = !!firstMissing(form) || !!firstInvalid(form) || !!finderBlocked(form);
      var unticked = !!agree && !agree.checked;
      var locked = unpicked || unavailable || unfilled || unticked;
      express.classList.toggle('is-locked', locked);
      if (locked) express.setAttribute('inert', '');
      else express.removeAttribute('inert');
      if (hint) {
        hint.hidden = !locked;
        hint.textContent = unpicked
          ? 'Pick an option above to use Shop Pay.'
          : unavailable
            ? 'Choose an available option above to use Shop Pay.'
          : unfilled
            ? 'Fill in the fields above to use Shop Pay.'
            : 'Tick the box above to use Shop Pay.';
      }
    }

    zone.addEventListener('change', sync);
    zone.addEventListener('input', sync);
    // The locked wrapper takes no taps (inert), so a tap on it lands on the zone.
    zone.addEventListener('click', function (event) {
      if (!express.classList.contains('is-locked')) return;
      var r = express.getBoundingClientRect();
      if (event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom) {
        track('shoppay_locked_tap');
      }
    });
    sync();
  });

  function Emblem(root) {
    this.root = root;
    this.canvas = root.querySelector('canvas');
    if (!this.canvas || !this.canvas.getContext) throw new Error('emblem preview without a canvas');
    this.ctx = this.canvas.getContext('2d');
    this.color = root.getAttribute('data-color') || '#F4F7FF';
    this.colorName = root.getAttribute('data-color-name') || 'white';
    this.mode = root.getAttribute('data-mode') || 'text';
    this.cycle = root.getAttribute('data-cycle') === 'true';
    this.image = null;
    this.text = (root.getAttribute('data-text') || 'LOW KEY').slice(0, 24);
    this.presets = (root.getAttribute('data-presets') || '').split(',').map(function (p) {
      return p.trim().slice(0, 24);
    }).filter(Boolean);
    if (!this.presets.length) this.presets = [this.text];
    this.start = performance.now();
    this.visible = true;
    this.paused = false;
    this.pausedAt = 0;
    this.scrolling = false;
    this.raf = null;
    this.resize();
    var self = this;
    window.addEventListener('resize', function () { self.resize(); self.draw(); });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
        self.loop();
      }).observe(root);
    }
    document.addEventListener('visibilitychange', function () { self.loop(); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { self.loop(); });

    var pauseBtn = root.querySelector('[data-emblem-pause]') || document.querySelector('[data-emblem-pause="' + root.id + '"]');
    if (pauseBtn) {
      pauseBtn.addEventListener('click', function () {
        self.setPaused(!self.paused);
        pauseBtn.setAttribute('aria-pressed', String(self.paused));
        pauseBtn.textContent = self.paused ? 'Play animation' : 'Pause animation';
      });
    }

    root.setAttribute('data-live', 'true');
    this.label();
    this.loop();
  }

  Emblem.prototype.setText = function (text) {
    this.text = String(text || '').slice(0, 24) || ' ';
    this.label();
  };

  Emblem.prototype.setColor = function (hex, name) {
    this.color = hex;
    this.colorName = name || this.colorName;
    this.label();
  };

  Emblem.prototype.setMode = function (mode) {
    if (MODES.indexOf(mode) === -1 && !(mode === 'image' && this.image)) return;
    this.mode = mode;
    this.cycle = false;
    this.restart();
    this.label();
  };

  Emblem.prototype.restart = function () {
    this.start = performance.now();
    if (this.paused) this.pausedAt = this.start;
  };

  Emblem.prototype.setImage = function (img) {
    this.image = img;
    this.setMode('image');
  };

  Emblem.prototype.setPaused = function (paused) {
    if (paused === this.paused) return;
    if (paused) {
      this.pausedAt = performance.now();
    } else {
      this.start += performance.now() - this.pausedAt;
    }
    this.paused = paused;
    this.loop();
  };

  Emblem.prototype.stopCycle = function () {
    if (!this.cycle) return;
    this.cycle = false;
    this.mode = 'text';
    this.restart();
    this.label();
  };

  // While cycling: each quick-tap text held still, then the rings, then again.
  Emblem.prototype.frameState = function (t) {
    if (!this.cycle) return { mode: this.mode, text: this.text, t: t };
    var span = this.presets.length * CYCLE_TEXT;
    var local = t % (span + CYCLE_RINGS);
    if (local < span) return { mode: 'text', text: this.presets[Math.floor(local / CYCLE_TEXT)], t: 0 };
    return { mode: 'rings', text: this.text, t: local - span };
  };

  Emblem.prototype.isMoving = function () {
    if (this.cycle) return true;
    if (this.mode === 'image') return false;
    if (this.mode === 'text') return this.scrolling;
    return true;
  };

  Emblem.prototype.label = function () {
    if (this.mode === 'image' && !this.cycle) {
      this.canvas.setAttribute('aria-label', 'Emblem preview showing your image');
      return;
    }
    var what = this.cycle ? 'cycling through sample designs' : 'showing ' + MODE_NAMES[this.mode];
    var text = this.mode === 'text' && !this.cycle ? ', text "' + this.text.trim() + '"' : '';
    this.canvas.setAttribute('aria-label', 'Emblem preview in ' + this.colorName + ', ' + what + text);
  };

  Emblem.prototype.resize = function () {
    var size = this.canvas.clientWidth || 320;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(size * dpr);
    this.canvas.height = Math.round(size * dpr);
  };

  // Short text is shrunk until it fits and sits still. Only long typed text scrolls, first letter first.
  Emblem.prototype.drawText = function (ctx, w, t, text, fitWhole) {
    var size = w * 0.36;
    var max = w * 0.74;
    ctx.font = '700 ' + size + 'px ' + FONT;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    var width = ctx.measureText(text).width;
    if (width > max) {
      var fitted = size * max / width;
      if (fitWhole || reduceMotion || fitted >= w * 0.17) {
        size = fitted;
        ctx.font = '700 ' + size + 'px ' + FONT;
        width = ctx.measureText(text).width;
      }
    }
    var scrolls = width > max + 1;
    var x = scrolls ? w * 0.13 - ((t * w * 0.42) % (width + w * 0.2)) : (w - width) / 2;
    ctx.shadowColor = this.color;
    ctx.shadowBlur = w * 0.05;
    ctx.fillStyle = this.color;
    ctx.fillText(text, x, w * 0.52);
    ctx.shadowBlur = 0;
    return scrolls;
  };

  Emblem.prototype.drawRings = function (ctx, w, t) {
    var c = w / 2, gap = w * 0.12;
    ctx.strokeStyle = this.color;
    ctx.lineWidth = w * 0.028;
    ctx.shadowColor = this.color;
    ctx.shadowBlur = w * 0.04;
    for (var k = 0; k < 6; k++) {
      var r = ((t * w * 0.16) + k * gap) % (gap * 6);
      ctx.globalAlpha = Math.max(0, 1 - r / (w * 0.5));
      ctx.beginPath(); ctx.arc(c, c, r, 0, Math.PI * 2); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
  };

  Emblem.prototype.drawSweep = function (ctx, w, t) {
    var c = w / 2, a = t * 2.4, steps = 36;
    ctx.fillStyle = this.color;
    for (var i = 0; i < steps; i++) {
      var from = a - (i + 1) * 0.045, to = a - i * 0.045;
      ctx.globalAlpha = Math.pow(1 - i / steps, 2);
      ctx.beginPath(); ctx.moveTo(c, c); ctx.arc(c, c, w * 0.5, from, to + 0.01); ctx.closePath(); ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.beginPath(); ctx.arc(c, c, w * 0.035, 0, Math.PI * 2); ctx.fill();
  };

  Emblem.prototype.drawBars = function (ctx, w, t) {
    var n = 9, gap = w * 0.02, bw = (w * 0.78 - gap * (n - 1)) / n, x0 = w * 0.11;
    ctx.fillStyle = this.color;
    ctx.shadowColor = this.color;
    ctx.shadowBlur = w * 0.03;
    for (var i = 0; i < n; i++) {
      var h = 0.18 + 0.32 * (1 + Math.sin(t * 3.1 + i * 0.9)) / 2 + 0.3 * (1 + Math.sin(t * 5.3 + i * 1.7)) / 2;
      var bh = h * w * 0.7;
      var x = x0 + i * (bw + gap), y = w * 0.86 - bh;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(x, y, bw, bh, bw * 0.3); else ctx.rect(x, y, bw, bh);
      ctx.fill();
    }
    ctx.shadowBlur = 0;
  };

  // The whole logo, centred inside the circle: wide wordmarks keep both ends.
  Emblem.prototype.drawImage = function (ctx, w) {
    var img = this.image, box = w * 0.72, scale = Math.min(box / img.width, box / img.height);
    var dw = img.width * scale, dh = img.height * scale;
    ctx.drawImage(img, (w - dw) / 2, (w - dh) / 2, dw, dh);
  };

  // asFile: a clean square with no circle clip and no glass highlight, text shrunk to fit whole.
  Emblem.prototype.paint = function (ctx, w, t, asFile) {
    ctx.save();
    ctx.clearRect(0, 0, w, w);
    if (!asFile) {
      ctx.beginPath(); ctx.arc(w / 2, w / 2, w / 2, 0, Math.PI * 2); ctx.clip();
    }
    ctx.fillStyle = '#030304';
    ctx.fillRect(0, 0, w, w);
    var state = this.frameState(t);
    var scrolls = false;
    if (state.mode === 'image' && this.image) this.drawImage(ctx, w);
    else if (state.mode === 'rings') this.drawRings(ctx, w, state.t);
    else if (state.mode === 'sweep') this.drawSweep(ctx, w, state.t);
    else if (state.mode === 'bars') this.drawBars(ctx, w, state.t);
    else scrolls = this.drawText(ctx, w, state.t, state.text, asFile);
    if (!asFile) {
      var glass = ctx.createLinearGradient(0, 0, w, w);
      glass.addColorStop(0, 'rgba(255,255,255,0.10)');
      glass.addColorStop(0.45, 'rgba(255,255,255,0)');
      ctx.fillStyle = glass;
      ctx.fillRect(0, 0, w, w);
    }
    ctx.restore();
    return scrolls;
  };

  Emblem.prototype.time = function (now) {
    if (reduceMotion) return 1.1;
    var stamp = this.paused ? this.pausedAt : (now || performance.now());
    return Math.max(0, stamp - this.start) / 1000;
  };

  Emblem.prototype.draw = function (now) {
    this.scrolling = this.paint(this.ctx, this.canvas.width, this.time(now), false);
  };

  Emblem.prototype.exportStill = function () {
    var canvas = document.createElement('canvas');
    canvas.width = FILE_SIZE;
    canvas.height = FILE_SIZE;
    this.paint(canvas.getContext('2d'), FILE_SIZE, this.time(), true);
    return canvas;
  };

  Emblem.prototype.loop = function () {
    var self = this;
    this.draw();
    var run = this.visible && !document.hidden && !reduceMotion && !this.paused && this.isMoving();
    if (!run) {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
      return;
    }
    if (this.raf) return;
    var last = 0;
    function frame(now) {
      if (self.cycle && self.time(now) > CYCLE_STOP) self.stopCycle();
      if (now - last > 30) { self.draw(now); last = now; }
      if (!self.isMoving()) { self.raf = null; return; }
      self.raf = requestAnimationFrame(frame);
    }
    this.raf = requestAnimationFrame(frame);
  };

  /* ---------- Saving a file to the phone: share sheet on touch devices, download elsewhere ---------- */

  function saveFile(file, url, statusEl, onSaved) {
    function say(message) { if (statusEl) statusEl.textContent = message; }
    function download() {
      var link = document.createElement('a');
      if (!('download' in link)) {
        say('Press and hold the picture below, then Save.');
        return;
      }
      link.href = url;
      link.download = file.name || FILE_NAME;
      document.body.appendChild(link);
      link.click();
      link.remove();
      say('Saved ' + (file.name || FILE_NAME) + '. Didn\'t see it? Press and hold the picture below, then Save.');
      if (onSaved) onSaved();
    }
    var touch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    var canShare = false;
    try { canShare = touch && navigator.share && navigator.canShare && navigator.canShare({ files: [file] }); } catch (e) { canShare = false; }
    if (!canShare) {
      download();
      return;
    }
    navigator.share({ files: [file] }).then(function () {
      say('Saved. Nothing is uploaded.');
      if (onSaved) onSaved();
    }, function (err) {
      if (err && err.name === 'AbortError') return;
      say('Press and hold the picture below, then Save.');
    });
  }

  window.MinkMods.saveFile = saveFile;

  var emblems = {};
  each('[data-emblem]', function (root) {
    emblems[root.id] = new Emblem(root);
  });

  each('[data-emblem-controls]', function (panel) {
    var emblem = emblems[panel.getAttribute('data-emblem-controls')];
    if (!emblem) return;
    var textInput = panel.querySelector('[data-emblem-text]');
    var fileInput = panel.querySelector('[data-emblem-file]');
    var imagePill = panel.querySelector('[data-emblem-image-pill]');
    var fileStatus = panel.querySelector('[data-emblem-file-status]');
    var saveBtn = panel.querySelector('[data-emblem-save]');
    var saveStatus = panel.querySelector('[data-emblem-save-status]');
    var savedImg = panel.querySelector('[data-emblem-saved]');
    var nextBtn = panel.querySelector('[data-emblem-next]');
    var inAppHint = panel.querySelector('[data-emblem-inapp]');
    if (inAppHint && inApp) inAppHint.hidden = false;
    var saved = { file: null, url: '' };
    var saveTimer = null;

    // The email popup waits while the demo is in use.
    function touched() {
      window.MinkMods.demoTouched = Date.now();
    }
    panel.addEventListener('pointerdown', touched);
    panel.addEventListener('input', touched);
    panel.addEventListener('change', touched);
    emblem.root.addEventListener('pointerdown', touched);

    // In the TikTok and Instagram browsers "Open in browser" copies the address, so the design rides in it:
    // text, color and effect only, after the #. An uploaded image never goes in the address.
    function writeDesign() {
      if (!inApp || !window.history || !history.replaceState || emblem.mode === 'image' || emblem.cycle) return;
      // cart.js owns the fragment while the cart is on screen.
      if (/mm_(cart|more|rest)=/.test(location.hash || '')) return;
      try {
        history.replaceState(
          history.state,
          '',
          location.pathname + location.search + '#d=t:' + encodeURIComponent(emblem.text.trim()) + '|c:' + emblem.color.slice(1) + '|m:' + emblem.mode
        );
      } catch (e) {
        /* the design still saves as a file */
      }
    }

    function sizeFieldset() {
      return document.querySelector('[data-product-zone] [data-option-fieldset]');
    }

    // The home hero's buy button, which is only on the page while the emblem is live.
    function buyUrl() {
      var link = document.querySelector('[data-hero-buy] a[href]');
      return link ? link.getAttribute('href') : '';
    }

    // After a save, the next step is picking a size: on this page when it has the size pills, else on the product page.
    function afterSave() {
      if (nextBtn && (sizeFieldset() || buyUrl())) nextBtn.hidden = false;
      document.dispatchEvent(new CustomEvent('minkmods:design-saved'));
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
        Shopify.analytics.publish('minkmods:design_saved', { page: location.pathname });
      }
    }

    function syncModes() {
      panel.querySelectorAll('[data-emblem-mode]').forEach(function (r) { r.checked = r.value === emblem.mode && !emblem.cycle; });
    }

    // The file is built ahead of the tap: phones only open the share sheet straight from a tap.
    function buildFile() {
      if (!saveBtn) return;
      if (emblem.cycle) {
        emblem.stopCycle();
        syncModes();
        emblem.loop();
      }
      var canvas = emblem.exportStill();
      if (!canvas.toBlob) return;
      canvas.toBlob(function (blob) {
        if (!blob) return;
        var file = blob;
        try { file = new File([blob], FILE_NAME, { type: 'image/jpeg' }); } catch (e) { file = blob; }
        if (saved.url) URL.revokeObjectURL(saved.url);
        saved.file = file;
        saved.url = URL.createObjectURL(blob);
        if (savedImg) {
          savedImg.src = saved.url;
          savedImg.hidden = false;
        }
      }, 'image/jpeg', 0.92);
    }

    function queueFile() {
      writeDesign();
      if (!saveBtn) return;
      saved.file = null;
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = setTimeout(buildFile, 300);
    }

    if (saveBtn) {
      saveBtn.addEventListener('pointerdown', function () {
        if (!saved.file) {
          if (saveTimer) clearTimeout(saveTimer);
          buildFile();
        }
      });
      saveBtn.addEventListener('click', function () {
        if (!saved.file) {
          buildFile();
          if (saveStatus) saveStatus.textContent = 'Getting your design ready. Tap Save again.';
          return;
        }
        saveFile(saved.file, saved.url, saveStatus, afterSave);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        var size = sizeFieldset();
        if (!size) {
          if (buyUrl()) location.href = buyUrl();
          return;
        }
        size.scrollIntoView({ block: 'center' });
        var first = size.querySelector('input');
        if (first) first.focus({ preventScroll: true });
      });
    }

    if (textInput) {
      textInput.addEventListener('input', function () {
        emblem.setText(textInput.value || textInput.placeholder);
        emblem.setMode('text');
        syncModes();
        emblem.loop();
        queueFile();
      });
    }

    // Phones: the keyboard hides the big preview, so a small copy floats under the header while the buyer types.
    if (textInput && window.matchMedia) {
      var stage = emblem.root.parentNode;
      var floatEmblem = function (on) {
        if (on && window.matchMedia('(min-width: 990px)').matches) return;
        stage.style.minHeight = on ? stage.offsetHeight + 'px' : '';
        emblem.root.classList.toggle('is-floating', on);
        emblem.resize();
        emblem.draw();
      };
      textInput.addEventListener('focus', function () { floatEmblem(true); });
      textInput.addEventListener('blur', function () { floatEmblem(false); });
    }

    panel.querySelectorAll('[data-emblem-preset]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var value = btn.getAttribute('data-emblem-preset');
        if (textInput) textInput.value = value;
        emblem.setText(value);
        emblem.setMode('text');
        syncModes();
        emblem.loop();
        queueFile();
      });
    });

    if (fileInput) {
      fileInput.addEventListener('change', function () {
        var file = fileInput.files && fileInput.files[0];
        if (!file) return;
        if (!/^image\//.test(file.type)) {
          if (fileStatus) fileStatus.textContent = 'That file isn\'t an image. Try a PNG or JPG.';
          return;
        }
        var url = URL.createObjectURL(file);
        var img = new Image();
        img.onload = function () {
          emblem.setImage(img);
          if (imagePill) imagePill.hidden = false;
          syncModes();
          emblem.loop();
          if (fileStatus) fileStatus.textContent = 'Showing your image. It stays on this device.';
          queueFile();
          track('emblem_upload');
        };
        img.onerror = function () {
          if (fileStatus) fileStatus.textContent = 'Couldn\'t open that image. Try a PNG or JPG.';
        };
        img.src = url;
      });
    }

    panel.addEventListener('change', function (event) {
      var input = event.target;
      if (input.hasAttribute('data-emblem-color')) {
        emblem.setColor(input.value, input.getAttribute('data-color-name'));
      } else if (input.hasAttribute('data-emblem-mode')) {
        emblem.setMode(input.value);
      } else {
        return;
      }
      emblem.loop();
      queueFile();
    });

    // A design carried over in the address: text up to 24 characters, one of the listed colors, one of the effects.
    var carried = /(?:^#|&)d=t:([^|&]*)\|c:([0-9A-Fa-f]{6})\|m:([a-z]+)/.exec(location.hash || '');
    if (carried) {
      var carriedText = '';
      try { carriedText = decodeURIComponent(carried[1]).slice(0, 24); } catch (e) { carriedText = ''; }
      var swatch = Array.prototype.find.call(panel.querySelectorAll('[data-emblem-color]'), function (input) {
        return input.value.toLowerCase() === '#' + carried[2].toLowerCase();
      });
      if (swatch) {
        swatch.checked = true;
        emblem.setColor(swatch.value, swatch.getAttribute('data-color-name'));
      }
      if (carriedText.trim()) {
        emblem.setText(carriedText);
        if (textInput) textInput.value = carriedText;
      }
      emblem.setMode(MODES.indexOf(carried[3]) !== -1 ? carried[3] : 'text');
      syncModes();
      emblem.loop();
      queueFile();
    }
  });

  each('[data-jump-try]', function (link) {
    if (document.getElementById('try')) link.hidden = false;
  });

  /* ---------- Plate toggle ---------- */

  each('[data-plate-toggle]', function (wrap) {
    var stage = document.getElementById(wrap.getAttribute('data-plate-toggle'));
    if (!stage) return;
    wrap.addEventListener('change', function (event) {
      stage.setAttribute('data-look', event.target.value);
    });
  });

  function money(cents, format) {
    if (window.ThemeCart && window.ThemeCart.formatMoney) return window.ThemeCart.formatMoney(cents);
    var amount = (Math.round(cents) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',').replace(/\.00$/, '');
    return String(format || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  /* ---------- Single vs set: the button adds the set's matching variant ---------- */

  each('[data-product-zone]', function (zone) {
    var choice = zone.querySelector('[data-set-choice]');
    var setJson = zone.querySelector('[data-set-variants]');
    if (!choice || !setJson) return;
    var format = zone.getAttribute('data-money-format');
    var idInput = zone.querySelector('[data-variant-id]');
    var jsonEl = (zone.closest('section') || document).querySelector('[data-variant-json]');
    var variants = [], setVariants = [];
    try { variants = JSON.parse(jsonEl.textContent); setVariants = JSON.parse(setJson.textContent); } catch (e) { return; }

    function selectedOptions() {
      var picked = [];
      zone.querySelectorAll('[data-option-fieldset]').forEach(function (fs) {
        var checked = fs.querySelector('input[type="radio"]:checked');
        picked.push(checked ? checked.value : null);
      });
      return picked;
    }

    function update() {
      var picked = selectedOptions();
      var wantSet = (choice.querySelector('input:checked') || {}).value === 'set';
      // Shopify's installments line prices this product only, so it steps aside while the set is picked.
      var terms = zone.querySelector('.pdp__terms');
      if (terms) terms.hidden = wantSet;
      // The sticky bar names what the button adds.
      var stickyTitle = document.querySelector('[data-sticky-title]');
      var pickedName = choice.getAttribute(wantSet ? 'data-set-name' : 'data-single-name');
      if (stickyTitle && pickedName) stickyTitle.textContent = pickedName;
      var base = variants.length === 1 ? variants[0] : variants.find(function (v) {
        return v.options.every(function (o, i) { return o === picked[i]; });
      });
      if (!base) return;
      var target = base;
      if (wantSet) {
        target = setVariants.find(function (v) { return v.options[0] === base.options[0] && v.available; });
      }
      var atc = zone.querySelector('[data-atc]');
      if (!target) {
        // No id left in the form, so neither the button nor the sticky bar can add the single by mistake.
        idInput.value = '';
        if (atc) { atc.disabled = true; atc.textContent = 'Set unavailable in this size'; }
        return;
      }
      idInput.value = target.id;
      if (atc && target.available) {
        atc.disabled = false;
        atc.removeAttribute('aria-disabled');
        atc.innerHTML = 'Add to cart, <span data-atc-total>' + money(target.price, format) + '</span>';
      } else if (atc) {
        atc.disabled = true;
        atc.textContent = 'Sold out';
      }
      var display = zone.querySelector('[data-price-display]');
      if (display) display.textContent = money(target.price, format);
      var sticky = document.querySelector('[data-sticky-price]');
      if (sticky) sticky.textContent = money(target.price, format);
    }

    zone.addEventListener('change', update);
  });

  /* ---------- Option pills show what each choice adds to the price ---------- */

  each('[data-product-zone]', function (zone) {
    var fieldsets = zone.querySelectorAll('[data-option-fieldset]');
    if (!fieldsets.length) return;
    var format = zone.getAttribute('data-money-format');
    var jsonEl = (zone.closest('section') || document).querySelector('[data-variant-json]');
    var variants = [];
    try { variants = JSON.parse(jsonEl.textContent); } catch (e) { return; }

    function picked() {
      return Array.prototype.map.call(fieldsets, function (fs) {
        var checked = fs.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });
    }

    function find(options) {
      return variants.find(function (v) {
        return v.options.every(function (o, i) { return o === options[i]; });
      });
    }

    function update() {
      var current = picked();
      var base = current.indexOf(null) === -1 ? find(current) : null;
      fieldsets.forEach(function (fs, i) {
        var priced = false;
        fs.querySelectorAll('input[type="radio"]').forEach(function (input) {
          var out = input.parentNode.querySelector('[data-option-delta]');
          if (!out) return;
          var text = '';
          if (base && !input.checked) {
            var next = current.slice();
            next[i] = input.value;
            var alt = find(next);
            if (alt && alt.price !== base.price) {
              var diff = alt.price - base.price;
              text = (diff > 0 ? '+' : '−') + money(Math.abs(diff), format);
            }
          }
          out.textContent = text;
          if (text) priced = true;
        });
        // Every pill keeps room for a price, so the row doesn't shift under the thumb when the pick changes.
        if (priced) fs.setAttribute('data-has-delta', '');
        else fs.removeAttribute('data-has-delta');
      });
    }

    zone.addEventListener('change', update);
    update();
  });

  /* ---------- Size finder and typed-car hints ---------- */

  each('[data-fit-finder]', function (wrap) {
    var zone = wrap.closest('[data-product-zone]');
    var select = wrap.querySelector('[data-fit-finder-select]');
    var note = wrap.querySelector('[data-fit-finder-note]');
    if (!zone || !select) return;
    var vehicle = zone.querySelector('[name="properties[Vehicle]"]');
    var filled = '';

    var blocked = false;

    function block() {
      var atc = zone.querySelector('[data-atc]');
      if (!atc) return;
      blocked = true;
      atc.disabled = true;
      atc.textContent = 'Doesn\'t fit this car';
    }

    // Hands the button back to the variant picker once a car that fits (or no car) is picked.
    function unblock() {
      if (!blocked) return;
      blocked = false;
      var checked = zone.querySelector('[data-option-fieldset] input[type="radio"]:checked');
      if (checked) {
        checked.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      var atc = zone.querySelector('[data-atc]');
      if (atc) {
        atc.disabled = false;
        atc.removeAttribute('aria-disabled');
        atc.textContent = atc.getAttribute('data-pick-label') || 'Select an option';
      }
    }

    select.addEventListener('change', function () {
      var value = select.value;
      var option = select.options[select.selectedIndex];
      if (note) note.textContent = '';
      if (value === 'none') {
        if (vehicle && vehicle.value === filled) {
          vehicle.value = '';
          filled = '';
          vehicle.dispatchEvent(new Event('input', { bubbles: true }));
        }
        if (note) note.textContent = select.getAttribute('data-nofit') || '';
        return;
      }
      if (value === '?') {
        if (note) note.textContent = 'Measure the rear badge edge to edge and choose its size below. Unsure? Use the fitment guide or ask us before ordering.';
        unblock();
        return;
      }
      if (!value) {
        unblock();
        return;
      }
      var radio = Array.prototype.find.call(zone.querySelectorAll('[data-option-fieldset] input[type="radio"]'), function (input) {
        return input.value === value;
      });
      if (radio && !radio.checked) {
        blocked = false;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        unblock();
      }
      var car = option ? option.getAttribute('data-car') || '' : '';
      if (vehicle && car && (!vehicle.value.trim() || vehicle.value === filled)) {
        vehicle.value = car;
        filled = car;
        vehicle.dispatchEvent(new Event('input', { bubbles: true }));
      }
      if (note) note.textContent = 'Suggested size: ' + value + '. Add your exact year and model below; we confirm the fit before shipping.';
    });

    // Listed last, so it runs after the variant picker has re-enabled the button.
    zone.addEventListener('change', function () {
      if (select.value === 'none') block();
    });
  });

  // Chassis codes first: each rule is [pattern, message, size it means (if any)].
  var FIT_HINTS = {
    'bmw-badge': [
      [/\b(honda|civic|accord|toyota|camry|corolla|supra|ford|mustang|dodge|charger|challenger|chevy|chevrolet|camaro|tesla|model [3ysx]|subaru|wrx|nissan|hyundai|kia|audi|mercedes|benz|lexus|acura|vw|volkswagen|mazda)\b/i,
        'This emblem fits BMW round badges only. Not a BMW? Email us before you order.', ''],
      [/\b(g2[236]|i4|g8[23]|f06|f1[23]|f2[01]|e8[17]|e86|e93|f39|x2)\b/i,
        'This car is not compatible with this emblem. Ask us before ordering.', ''],
      [/\b(g01|g45|g0[5-7]|f9[5-7]|g42|g87|g29|g60|g90)\b/i, 'This chassis normally uses 82 mm; we confirm before shipping.', '82 mm'],
      [/\b(e46|e90|f3[0-3]|f36|g20|f80|f2[23]|f87|g30|f90|g32)\b/i, 'This chassis normally uses 74 mm; we confirm before shipping.', '74 mm']
    ]
  };

  // A model name the buyer types ("330i", "M3", "5 Series"), as the key the size finder's list uses.
  function modelKey(text) {
    var m = /\bx([1-7])\s?m\b/i.exec(text);
    if (m) return { key: 'x' + m[1] + 'm', name: 'X' + m[1] + ' M' };
    m = /\b(x[1-7]|z4|i4)\b/i.exec(text);
    if (m) return { key: m[1].toLowerCase(), name: m[1].charAt(0).toUpperCase() === 'I' ? 'i4' : m[1].toUpperCase() };
    m = /\bm([2-8])\b/i.exec(text);
    if (m) return { key: 'm' + m[1], name: 'M' + m[1] };
    m = /\b(m?[1-8]\d{2}[a-z]{0,2})\b/i.exec(text);
    if (m) return { key: m[1].replace(/^m/i, '').charAt(0) + ' series', name: m[1] };
    m = /\b([1-8])[\s-]?series\b/i.exec(text);
    if (m) return { key: m[1] + ' series', name: m[1] + ' Series' };
    return null;
  }

  // The finder's own lines, read as: which models, which years, which size. The typed-car hint only ever
  // repeats what this list says, and stays quiet when the list gives two answers.
  function finderCars(select) {
    return Array.prototype.map.call(select ? select.options : [], function (option) {
      var label = option.textContent || '';
      var keys = [];
      var re = /\b(\d) Series\b|\b(M\d)\b|\b(X\d)( M)?\b|\b(Z4|i4)\b/g;
      var m = re.exec(label);
      while (m) {
        if (m[1]) keys.push(m[1] + ' series');
        else if (m[2]) keys.push(m[2].toLowerCase());
        else if (m[3]) keys.push(m[3].toLowerCase() + (m[4] ? 'm' : ''));
        else if (m[5]) keys.push(m[5].toLowerCase());
        m = re.exec(label);
      }
      var years = /\b((?:19|20)\d{2})\s*-\s*((?:19|20)\d{2}|on)\b/.exec(label);
      return {
        size: option.value,
        keys: keys,
        from: years ? Number(years[1]) : 0,
        to: years && years[2] !== 'on' ? Number(years[2]) : 9999
      };
    }).filter(function (car) { return car.keys.length && car.size && car.size !== '?'; });
  }

  each('[data-fit-hints]', function (input) {
    var rules = FIT_HINTS[input.getAttribute('data-fit-hints')];
    var field = input.closest('.pdp__field');
    var zone = input.closest('[data-product-zone]');
    var out = field && field.querySelector('[data-fit-hint]');
    if (!rules || !out) return;
    var finder = zone && zone.querySelector('[data-fit-finder-select]');
    var finderNote = zone && zone.querySelector('[data-fit-finder-note]');
    var cars = finderCars(finder);

    function lookup(text) {
      for (var i = 0; i < rules.length; i++) {
        var hit = rules[i][0].exec(text);
        if (hit) return { text: rules[i][1], size: rules[i][2], name: rules[i][2] ? hit[1].toUpperCase() : '', noFit: !rules[i][2] };
      }
      var model = modelKey(text);
      if (!model || !cars.length) return null;
      var year = /\b((?:19|20)\d{2})\b/.exec(text);
      var matches = cars.filter(function (car) {
        return car.keys.indexOf(model.key) !== -1 && (!year || (Number(year[1]) >= car.from && Number(year[1]) <= car.to));
      });
      if (!matches.length || matches.some(function (car) { return car.size !== matches[0].size; })) return null;
      var size = matches[0].size;
      var name = (year ? year[1] + ' ' : '') + model.name;
      if (size === 'none') return { text: (finder && finder.getAttribute('data-nofit')) || 'This one won\'t fit that car.', size: '', name: '', noFit: true };
      return { text: 'A ' + name + ' normally takes ' + size + '.', size: size, name: name };
    }

    function update() {
      input.removeAttribute('data-fit-blocked');
      if (finder && finder.value === 'none') {
        out.textContent = '';
        return;
      }
      var hint = lookup(input.value);
      if (hint && hint.noFit) input.setAttribute('data-fit-blocked', hint.text);
      var checked = zone && zone.querySelector('[data-option-fieldset][data-option-index="0"] input[type="radio"]:checked');
      var text = hint ? hint.text : '';
      if (hint && hint.size && checked && checked.value !== hint.size) {
        text = 'A ' + hint.name + ' normally takes ' + hint.size + ', you picked ' + checked.value + '. We\'ll check before it ships.';
      }
      out.textContent = hint && hint.noFit && fieldHasError(input) ? '' : text;
      // A new typed car cannot leave a contradictory size recommendation on screen.
      if (hint && finderNote && (hint.noFit || (hint.size && finder && /mm$/.test(finder.value) && finder.value !== hint.size))) finderNote.textContent = '';
      if (fieldHasError(input) && !fieldProblem(input)) clearFieldError(input);
    }

    input.addEventListener('input', update);
    if (zone) zone.addEventListener('change', update);
  });

  /* ---------- "It's a gift": the buyer gives the other person's car ---------- */

  each('[data-gift-toggle]', function (box) {
    var field = box.closest('.pdp__field');
    var input = field && field.querySelector('[name="properties[Vehicle]"]');
    var help = field && field.querySelector('[data-vehicle-help]');
    if (!input) return;
    var own = {
      placeholder: input.getAttribute('placeholder') || '',
      message: input.getAttribute('data-required-message') || '',
      help: help ? help.textContent : ''
    };
    box.addEventListener('change', function () {
      var gift = box.checked;
      input.setAttribute('placeholder', gift ? 'Their car, e.g. 2019 BMW 330i' : own.placeholder);
      input.setAttribute('data-required-message', gift
        ? 'Add their car\'s year and model. That\'s enough for us to pick the size.'
        : own.message);
      if (help) {
        help.textContent = gift
          ? 'Add their year, make and model. We email you to confirm the size. If it cannot fit, we refund before shipping.'
          : own.help;
      }
    });
  });

  /* ---------- Gift card: send it to someone else by email ---------- */

  function localYmd(d) {
    function pad(n) { return n < 10 ? '0' + n : String(n); }
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  each('[data-gift-card]', function (wrap) {
    var box = wrap.querySelector('[data-gift-card-toggle]');
    var fields = wrap.querySelector('[data-gift-card-fields]');
    if (!box || !fields) return;
    var inputs = fields.querySelectorAll('input, textarea');
    var email = fields.querySelector('[data-gift-card-email]');
    var date = fields.querySelector('[data-gift-card-date]');
    var offset = fields.querySelector('[data-gift-card-offset]');
    inputs.forEach(function (input) { input.setAttribute('data-name', input.getAttribute('name')); });

    function sync() {
      var on = box.checked;
      fields.hidden = !on;
      // Unticked fields lose their name as well as going disabled, so a typed address is never sent by mistake.
      inputs.forEach(function (input) {
        input.disabled = !on;
        if (on) input.setAttribute('name', input.getAttribute('data-name'));
        else input.removeAttribute('name');
      });
      if (email) {
        if (on) email.setAttribute('data-required-property', '');
        else email.removeAttribute('data-required-property');
      }
      if (date && on && !date.getAttribute('min')) {
        var today = new Date();
        var last = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 90);
        date.setAttribute('min', localYmd(today));
        date.setAttribute('max', localYmd(last));
      }
      if (offset) offset.value = on ? String(new Date().getTimezoneOffset()) : '';
    }

    box.addEventListener('change', sync);
    sync();
  });

  /* ---------- Delivery date range from business days (the buyer's own date, minus weekends and listed holidays) ---------- */

  function skipDates(text) {
    var set = {};
    String(text || '').split(/[\s,;]+/).forEach(function (part) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(part)) set[part] = true;
    });
    return set;
  }

  function addBusinessDays(date, days, skip) {
    var d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    var guard = 0;
    while (days > 0 && guard < 500) {
      d.setDate(d.getDate() + 1);
      guard += 1;
      if (d.getDay() !== 0 && d.getDay() !== 6 && !skip[localYmd(d)]) days -= 1;
    }
    return d;
  }

  function sharedSkips() {
    var shared = document.querySelector('[data-business-skips]');
    return shared ? shared.getAttribute('data-business-skips') : '';
  }

  // "Oct 14 to Oct 23" for an order placed today, or '' when there is no window to state.
  function etaDates(min, max, skipText) {
    min = parseInt(min, 10);
    max = parseInt(max, 10);
    if (!(min > 0) || !(max >= min)) return null;
    var skip = skipDates(skipText || sharedSkips());
    var now = new Date();
    return { now: now, first: addBusinessDays(now, min, skip), last: addBusinessDays(now, max, skip) };
  }

  function etaText(dates) {
    var fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
    return fmt.format(dates.first) + ' to ' + fmt.format(dates.last);
  }

  window.MinkMods.etaRange = function (min, max) {
    var dates = etaDates(min, max, '');
    return dates ? etaText(dates) : '';
  };

  each('[data-delivery-estimate]', function (el) {
    var dates = etaDates(el.getAttribute('data-min'), el.getAttribute('data-max'), el.getAttribute('data-skip'));
    if (!dates) return;
    var range = etaText(dates);
    var now = dates.now;
    var first = dates.first;
    var last = dates.last;

    // Nov 1 to Dec 24 only: say so plainly when a gift may miss Christmas. Never a promise that it will make it.
    var note = '';
    var year = now.getFullYear();
    if (now >= new Date(year, 10, 1) && now < new Date(year, 11, 25)) {
      if (first > new Date(year, 11, 24)) note = 'Arrives after Christmas. ';
      else if (last > new Date(year, 11, 23)) note = 'May not arrive by Christmas. ';
    }

    el.textContent = 'Order today, arrives about ' + range + '. ' + note;
    var zone = el.closest('[data-product-zone]');
    var field = zone && zone.querySelector('[data-eta-property]');
    if (field) field.value = range;
  });

  // One-tap Add on a product card saves the same estimate as the product page.
  each('[data-eta-property][data-min]', function (field) {
    var dates = etaDates(field.getAttribute('data-min'), field.getAttribute('data-max'), '');
    if (dates) field.value = etaText(dates);
  });

  /* ---------- Promotion lines show only inside their dates ---------- */

  each('[data-promo]', function (el) {
    var start = new Date(el.getAttribute('data-start') || '').getTime();
    var end = new Date(el.getAttribute('data-end') || '').getTime();
    if (isNaN(start) || isNaN(end)) return;
    var timer = null;
    // Re-checked at each boundary and when the tab comes back, so a page left open never shows an ended offer.
    function sync() {
      var now = Date.now();
      el.hidden = !(now >= start && now < end);
      if (timer) clearTimeout(timer);
      var next = now < start ? start - now : now < end ? end - now : 0;
      if (next > 0) timer = setTimeout(sync, Math.min(next + 500, 2147483647));
    }
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) sync();
    });
    sync();
  });

  /* ---------- Hero buy bar on phones: from the hero button down to the next product section ---------- */

  guard(function () {
    var heroBuy = document.querySelector('[data-hero-buy]');
    var heroSticky = document.querySelector('[data-hero-sticky]');
    if (!heroBuy || !heroSticky) return;
    var heroStop = null;
    try { heroStop = document.querySelector(heroSticky.getAttribute('data-stop') || '#plates'); } catch (e) { heroStop = null; }
    var heroQueued = false;
    var syncHeroSticky = function () {
      heroQueued = false;
      var past = heroBuy.getBoundingClientRect().bottom < 0;
      var done = !!heroStop && heroStop.getBoundingClientRect().top < window.innerHeight * 0.6;
      var on = past && !done;
      heroSticky.classList.toggle('is-visible', on);
      document.body.classList.toggle('has-sticky-bar', on);
      document.documentElement.style.setProperty('--sticky-h', on ? heroSticky.offsetHeight + 'px' : '0px');
    };
    var queueHeroSticky = function () {
      if (heroQueued) return;
      heroQueued = true;
      requestAnimationFrame(syncHeroSticky);
    };
    heroSticky.hidden = false;
    window.addEventListener('scroll', queueHeroSticky, { passive: true });
    window.addEventListener('resize', queueHeroSticky);
    syncHeroSticky();
  });

  /* ---------- Wheel builder: the picked options, and the closest photo of this make when there is one ---------- */

  each('[data-wheel-build]', function (root) {
    var zone = root.closest('[data-product-zone]');
    if (!zone) return;
    var shot = root.querySelector('[data-wheel-shot]');
    var detail = root.querySelector('[data-wheel-detail]');
    var libraryEl = root.querySelector('[data-wheel-library]');
    var line = root.querySelector('[data-wheel-line]');
    var adds = root.querySelector('[data-wheel-adds]');
    var renderTag = root.querySelector('[data-render-tag]');
    var productTitle = zone.getAttribute('data-product-title') || 'Steering wheel';

    // The close-up goes under the photo, never over it.
    var photoBox = detail ? detail.closest('.pdp__main-media') : null;
    if (photoBox && photoBox.parentNode) photoBox.parentNode.insertBefore(detail, photoBox.nextSibling);

    var title = productTitle.toLowerCase();
    var make = 'bmw';
    if (title.indexOf('mercedes') !== -1) make = 'mercedes';
    else if (title.indexOf('audi') !== -1 || title.indexOf('vw') !== -1) make = 'audi';
    else if (/japanese|korean|honda|nissan|infiniti/.test(title)) make = 'jdm';
    else if (title.indexOf('american') !== -1) make = 'american';

    // Never another make's wheel: a page with no photo of its own make keeps what the page rendered.
    var library = [];
    if (libraryEl) {
      try { library = JSON.parse(libraryEl.textContent) || []; } catch (e) { library = []; }
    }
    library = library.filter(function (item) {
      return item && item.src && (!item.make || item.make === 'any' || item.make === make);
    });
    var hasPhotos = !!shot && library.some(function (item) { return !item.detail; });
    var shown = null;

    // No photo of this make in the list and none rendered: the product's own first photo, or no image at all.
    if (shot && !hasPhotos && !shot.getAttribute('src')) {
      var ownPhoto = zone.querySelector('[data-wheel-thumb]');
      if (ownPhoto) {
        shot.src = ownPhoto.getAttribute('data-wheel-thumb');
        shot.alt = ownPhoto.getAttribute('data-alt') || productTitle;
      } else {
        shot.hidden = true;
      }
    }

    function optionValue(part) {
      var sets = zone.querySelectorAll('[data-option-fieldset]');
      for (var i = 0; i < sets.length; i++) {
        var legend = sets[i].querySelector('legend');
        if (!legend || legend.textContent.toLowerCase().indexOf(part) === -1) continue;
        var checked = sets[i].querySelector('input[type="radio"]:checked');
        return checked ? checked.value : '';
      }
      return '';
    }

    function score(item, want) {
      if (item.detail) return -100;
      var s = 0;
      s += !!item.led === want.led ? 5 : -3;
      s += !!item.carbon === want.carbon ? 8 : -3;
      if (want.stitch && item.stitch === want.stitch) s += 4;
      if (item.full) s += 3;
      if (item.make === make) s += 10;
      return s;
    }

    function update() {
      var led = /^(led|yes)/i.test(optionValue('led'));
      var carbonPaddles = /carbon/i.test(optionValue('paddle'));
      var heat = /^(keep|yes)/i.test(optionValue('heat'));
      var stitchField = zone.querySelector('select[name="properties[Stitching color]"]');
      var stitch = stitchField ? stitchField.value.toLowerCase() : '';
      var best = null;
      var note = '';

      if (hasPhotos) {
        var want = { led: led, carbon: carbonPaddles, stitch: stitch };
        var bestScore = -999;
        library.forEach(function (item) {
          var n = score(item, want);
          if (n > bestScore) { bestScore = n; best = item; }
        });
        if (best) {
          // A product photo's own alt text is not a caption: drop the Render marker and a bare product title.
          note = String(best.note || '').replace('[render]', '').trim();
          if (note === productTitle) note = '';
          // Only when the best match changes, so a photo the buyer tapped stays up while they fill in the form.
          if (best !== shown) {
            shown = best;
            if (shot.getAttribute('src') !== best.src) {
              shot.removeAttribute('srcset');
              shot.src = best.src;
            }
            shot.alt = note || String(best.alt || '') || productTitle;
            if (renderTag) renderTag.hidden = !best.render;
          }
        }

        var closeup = null;
        if (best && stitch === 'red' && best.stitch !== 'red') {
          closeup = library.filter(function (item) { return item.detail && item.stitch === 'red'; })[0];
        } else if (best && led && !best.led) {
          closeup = library.filter(function (item) { return item.detail && item.led && !item.stitch; })[0];
        }
        if (detail) {
          if (closeup) {
            detail.hidden = false;
            detail.src = closeup.src;
            detail.alt = closeup.note || '';
          } else {
            detail.hidden = true;
            detail.removeAttribute('src');
          }
        }
      }

      if (line) line.textContent = note ? (best && best.render ? 'Render: ' : 'Photo: ') + note : 'Style preview. Your own airbag and buttons move over; they are not included.';

      if (!adds) return;
      var extras = [];
      if (led) extras.push('LED shift lights added');
      if (carbonPaddles) extras.push('Carbon paddles added');
      if (heat) extras.push('Keep my heated wheel; we confirm it for your car by email');
      if (stitch) extras.push(stitch.charAt(0).toUpperCase() + stitch.slice(1) + ' stitching');
      var picked = extras.length > 0;
      if (!picked) extras.push('Base wheel selected. Choose optional extras below.');
      adds.textContent = '';
      extras.forEach(function (text) {
        var li = document.createElement('li');
        if (picked) li.className = 'is-add';
        li.textContent = text;
        adds.appendChild(li);
      });
      // The list above sits by the photo, off screen on a phone while the options are tapped.
      var pickedLine = zone.querySelector('[data-wheel-picked]');
      if (pickedLine) {
        pickedLine.hidden = !picked;
        pickedLine.textContent = picked ? extras.join('. ') + '.' : '';
      }
    }

    zone.querySelectorAll('[data-wheel-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        if (!shot) return;
        shot.removeAttribute('srcset');
        shot.src = thumb.getAttribute('data-wheel-thumb');
        shot.alt = thumb.getAttribute('data-alt') || productTitle;
        var isRender = thumb.getAttribute('data-render') === 'true';
        if (renderTag) renderTag.hidden = !isRender;
        if (detail) detail.hidden = true;
        if (line) line.textContent = (isRender ? 'Render' : 'Photo') + ' of an example build. Your selected options are confirmed by email.';
      });
    });

    zone.addEventListener('change', update);
    update();
  });

  /* ---------- Shopify's installments line and pay buttons listen for a change on the variant id ---------- */

  each('[data-product-zone]', function (zone) {
    var idInput = zone.querySelector('[data-variant-id]');
    if (!idInput) return;
    var last = idInput.value;
    // Listed after every picker above, so the id is final by the time this runs.
    zone.addEventListener('change', function (event) {
      if (event.target === idInput || idInput.value === last) return;
      last = idInput.value;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  /* ---------- Render tag follows the selected gallery image ---------- */

  each('.pdp__thumb[data-render]', function (thumb) {
    thumb.addEventListener('click', function () {
      var tag = document.querySelector('[data-render-tag]');
      if (tag) tag.hidden = thumb.getAttribute('data-render') !== 'true';
    });
  });

  /* Real clip playback is managed by ugc-autoplay.js. */

  /* ---------- Opened from a cart link: the size and the car are filled in. Tick boxes never are. ---------- */

  guard(function () {
    var found = /(?:^#|&)mm_fill=([^&]*)/.exec(location.hash || '');
    if (!found) return;
    var fill = [];
    try { fill = JSON.parse(decodeURIComponent(found[1])); } catch (e) { fill = []; }
    var pick = Array.isArray(fill) && typeof fill[0] === 'string' ? fill[0].slice(0, 120) : '';
    var car = Array.isArray(fill) && typeof fill[1] === 'string' ? fill[1].slice(0, 120) : '';
    var zone = document.querySelector('[data-product-zone]');
    if (zone && pick) {
      var radio = Array.prototype.find.call(
        zone.querySelectorAll('[data-option-fieldset][data-option-index="0"] input[type="radio"]'),
        function (input) { return input.value === pick; }
      );
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    var vehicle = zone && zone.querySelector('[name="properties[Vehicle]"]');
    if (vehicle && car && !vehicle.value.trim()) {
      vehicle.value = car;
      vehicle.dispatchEvent(new Event('input', { bubbles: true }));
    }
    try {
      history.replaceState(history.state, '', location.pathname + location.search);
    } catch (e) {
      /* the fragment stays; filling the same values again changes nothing */
    }
  });

  /* ---------- Back, forward and reload: the browser puts the buyer's picks back, so read them again ---------- */

  window.addEventListener('pageshow', function (event) {
    var nav = window.performance && performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    if (!event.persisted && nav && nav.type === 'navigate') return;
    document.querySelectorAll('[data-product-zone]').forEach(function (zone) {
      var restored = [zone.querySelector('[data-option-fieldset] input[type="radio"]:checked')];
      zone.querySelectorAll('[data-set-choice] input:checked, [data-gift-toggle], [data-gift-card-toggle]').forEach(function (input) {
        restored.push(input);
      });
      restored.forEach(function (input) {
        if (input) input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    if (event.persisted && window.ThemeCart) window.ThemeCart.refresh().catch(function () {});
  });
})();
